import * as d3 from 'd3';
import * as idb from 'idb';

import type { Taxon } from 'js/ebird/taxonomy';
import type { Checklist, Location, Observation } from 'js/ebird/ebird';
import type * as dateish from 'js/dateish';
import { Countries } from 'data/geography';
import type { Country, County, State } from 'js/ebird/ebird';
import { locCountryCode } from 'js/ebird/ebird';
import { assertUnreachable } from 'js/asserts';

// TOOD: Most of the wrappers in here aren't needed since we load and filter
// on the all observations data in general
export class Database {
  readonly geography: GeographyDB = new GeographyDB()

  constructor(
    readonly db: DB,
    readonly taxonomy: TaxonomyDB
  ) {}

  async reset(): Promise<void> {
    await this.db.clear('observations');
    await this.db.clear('imports');
  }

  async allObservations(): Promise<Observation[]> {
    return await this.db.getAll("observations");
  }

  async doyObservations(doy: dateish.DoY): Promise<Observation[]> {
    const all = await this.allObservations(); // TODO: filter directly
    return all.filter(o => `${o.date.month}-${o.date.day}` === doy);
  }

  async doySpecies(doy: dateish.DoY): Promise<Observation[]> {
    const observations = await this.doyObservations(doy);
    return observations.filter(o => o.taxon.speciesCode != null);
  }

  async doySpeciesTable(doy: dateish.DoY): Promise<SpeciesTable> {
    return this.getSpeciesTable(o => `${o.date.month}-${o.date.day}` === doy);
  }

  async moySpeciesTable(moy: dateish.MoY): Promise<SpeciesTable> {
    return this.getSpeciesTable(o => o.date.month === moy);
  }

  async dateishSpeciesTable({ kind, value }: dateish.Any): Promise<SpeciesTable> {
    switch (kind) {
      case 'doy': return this.doySpeciesTable(value);
      case 'moy': return this.moySpeciesTable(value);
    }
    assertUnreachable('species table', kind);
  }

  async getSpeciesTable(fn: ((o: Observation) => boolean)): Promise<SpeciesTable> {
    const all = await this.allObservations(); // TODO: Get the year extents in a better way
    const [minYear, maxYear] = d3.extent(all, o => o.date.year);

    const observations = all.filter(fn)
      .filter(o => o.taxon.speciesCode != null);

    const table: Record<string, Set<number>> = {};
    for (const o of observations) {
      table[o.taxon.speciesCode!] ??= new Set();
      table[o.taxon.speciesCode!].add(o.date.year);
    }

    return {
      years: { min: minYear!, max: maxYear! },
      species: Object.entries(table).map(([ code, years ]) => ({ code, years: Array.from(years).sort((a, b) => a-b) })),
    }
  }

  async clearObservations(): Promise<unknown> {
    await this.db.clear("observations");
    return;
  }

  async importObservations(file: File): Promise<unknown> {
    await this.clearObservations(); // clear existing first...

    const rows = await parseRows(file);
    console.log('import-file: rows:', rows.length);

    // TODO: leverage more d3 time and format stuff
    // https://github.com/d3/d3-time
    // https://github.com/d3/d3-time-format#locale_format
    const parseDate = d3.timeParse("%Y-%m-%d");

    const observations: Observation[] = rows.map(r => {
      const checklist: Checklist = {
        id: r.checklistID,
        date: r.date,
        time: r.time,
        protocol: r.protocol,
        complete: !!parseInt(r.complete), // TODO: NaN
        duration: parseInt(r.durationMin), // TODO: NaN
        distance: parseFloat(r.distanceKM), // TODO: NaN
        area: parseFloat(r.areaHA), // TODO: NaN
        observers: parseInt(r.numObservers),
        comments: r.checklistComments,
      };

      const location: Location = {
        id: r.locationID,
        name: r.locationName,
        state: r.stateProvince,
        county: r.county,
        latitude: parseFloat(r.latitude),
        longitude: parseFloat(r.longitude),
      };

      const when = parseDate(checklist.date);
      if (!when) {
        throw new Error(`when: failed to parse ${checklist.date}`)
      }

      const [ year, month, day ] = checklist.date.split("-"); // TODO: Parse for real

      const observation: Observation = {
        checklist,
        location,
        taxon: this.taxonomy.scientific(r.scientificName),
        count: parseInt(r.count), // TODO: NaN/X
        details: r.observationDetails,
        date: {
          year: parseInt(year),
          month: month as dateish.MoY,
          day: day as dateish.DoM,
          when
        },
      };
      return observation;
    });

    const tx = this.db.transaction('observations', 'readwrite');
    const promises: Promise<any>[] = observations.map(o => tx.store.add(o));
    promises.push(tx.done);
    await Promise.all(promises);

    // TODO: record for imports

    return; // TODO
  }
}

export interface SpeciesTable {
  years: { min: number, max: number }
  species: { code: string, years: number[] }[]
}

export async function initDatabase(taxa: Taxon[]): Promise<Database> {
  console.log('initDatabase');
  const db = await openDB();
  const taxonomy = new TaxonomyDB(taxa);
  return new Database(db, taxonomy);
}

export interface Schema extends idb.DBSchema {
  observations: {
    key: number
    value: Observation
  }
  imports: {
    key: number
    value: unknown // TODO: Make this useful so that we now something is here?
  }
}

export type DB = idb.IDBPDatabase<Schema>

export async function parseRows(file: File): Promise<ObservationRow[]> {
  const text = await file.text();

  // Template row that can be reused in the parser below
  const row = Object.fromEntries(Columns.map(c => [c, ''])) as ObservationRow;
  return d3.csvParseRows(text, (r, i) => {
    if (i === 0) { return; }
    r.forEach((val, j) => row[Columns[j]] = val);
    return structuredClone(row);
  });
}

type ObservationRow = Record<typeof Columns[number], string>;

// MyEBirdData.csv Headers
const Columns = [
  'checklistID', // 0 "Submission ID",
  'commonName', //  1 "Common Name",
  'scientificName', //  2 "Scientific Name",
  'taxonOrder', //  3 "Taxonomic Order",
  'count', //  4 "Count",
  'stateProvince', //  5 "State/Province",
  'county', //  6 "County",
  'locationID', //  7 "Location ID",
  'locationName', //  8 "Location",
  'latitude', //  9 "Latitude",
  'longitude', // 10 "Longitude",
  'date', // 11 "Date",
  'time', // 12 "Time",
  'protocol', // 13 "Protocol",
  'durationMin', // 14 "Duration (Min)",
  'complete', // 15 "All Obs Reported",
  'distanceKM', // 16 "Distance Traveled (km)",
  'areaHA', // 17 "Area Covered (ha)",
  'numObservers', // 18 "Number of Observers",
  'breedingCode', // 19 "Breeding Code",
  'observationDetails', // 20 "Observation Details",
  'checklistComments', // 21 "Checklist Comments",
  'macaulayIDs', // 22 "ML Catalog Numbers"
] as const;

async function openDB(): Promise<DB> {
  // TODO: The schema is going to need to be handled
  // TODO: Is version 0 the initial version?
  // TODO: How can I go about testing this?
  return await idb.openDB<Schema>('birding-days', 1, {
    // https://github.com/jakearchibald/idb/issues/281
    async upgrade(db, oldVersion, newVersion, transaction, event) {
      console.warn(`todo:db.upgrade old:${oldVersion} new:${newVersion}`);
      if (oldVersion < 1) {
        // Observations don't have an intrinsic identifier
        db.createObjectStore('imports', { autoIncrement: true });
        db.createObjectStore('observations', { keyPath: 'id', autoIncrement: true });
      }
      if (oldVersion < 2) {
        // TODO: Update to the new version
        // db.createObjectStore('checklists', { keyPath: 'id' });
        // db.createObjectStore('locations', { keyPath: 'id' });
      }
    },
    blocked(currentVersion, blockedVersion, event) {
      console.warn(`todo:db.blocked current:${currentVersion} blocked:${blockedVersion}`);
    },
    blocking(currentVersion, blockedVersion, event) {
      console.warn(`todo:db.blocking current:${currentVersion} blocked:${blockedVersion}`);
    },
    terminated() {
      console.warn(`todo:db.terminated`);
    },
  });
}



export class TaxonomyDB {
  private idxCode: Map<string, Taxon>
  private idxSci: Map<string, Taxon>

  constructor(private taxa: Taxon[]) {
    this.idxCode = d3.index(taxa, t => t.taxonCode);
    this.idxSci = d3.index(taxa, t => t.scientificName);
  }

  code(id: string): Taxon {
    const taxon = this.idxCode.get(id);
    if (taxon) { return taxon; }
    throw new Error(`not found: code=${id}`);
  }
  scientific(name: string): Taxon {
    const taxon = this.idxSci.get(name);
    if (taxon) { return taxon; }
    throw new Error(`not found: scientific=${name}`);
  }
}

// GeographyDB is the relationship graph of the regions of the world
export class GeographyDB { // TODO: Add the non-country other regions
  readonly countries: GeoCountry[];

  readonly idxCountries: Map<string, GeoCountry> = new Map();
  readonly idxStates: Map<string, GeoState> = new Map();
  readonly idxCounties: Map<string, GeoCounty> = new Map();

  constructor() {
    this.countries = Countries.flatMap(({ code, name, states, counties }) => {
      const country: GeoCountry = { kind: 'country', code, name, states: [] };
      this.idxCountries.set(code, country);

      country.states = states.flatMap(s => {
        const state: GeoState = { kind: 'state', code: s.code, name: s.name, country, counties: [] };
        this.idxStates.set(s.code, state);

        state.counties = counties.filter(c => c.code.startsWith(s.code)).map(c => {
          const county: GeoCounty = { kind: 'county', code: c.code, name: c.name, state, country };
          this.idxCounties.set(c.code, county);

          return county;
        });

        return state;
      });
      return country;
    });
  }

  // findCounty by the data provided in the observations download
  findCounty(stateCode: string, countyName: string): GeoCounty {
    const state = this.idxStates.get(stateCode);
    return state!.counties.find(c => c.name === countyName)!;
  }

  // filterByLocations creates a new instance of the geography tree
  // TODO: Should this just return a new GeographyDB?
  filterByLocations(locations: Location[]): GeoCountry[] {
    const countries: Map<string, GeoCountry> = new Map();
    const states: Map<string, GeoState> = new Map();
    const counties: Set<string> = new Set();

    for (const loc of locations) {
      const cc = locCountryCode(loc);
      let country = countries.get(cc);
      if (null == country) {
        const { name } = this.idxCountries.get(cc)!;
        country = { kind: 'country', code: cc, name, states: [] },
        countries.set(cc, country);
      }

      // todo can state actually be the country? or can it be null?
      let state = states.get(loc.state);
      if (null == state) {
        const { name } = this.idxStates.get(loc.state)!;
        // TODO: Function to get the region?
        state = { kind: 'state', code: loc.state, name, country, counties: [] };
        states.set(loc.state, state);
        country.states.push(state);
      }

      if (loc.county) {
        const { code } = this.findCounty(loc.state, loc.county)!;
        if (!counties.has(code)) {
          counties.add(code);
          state.counties.push({ kind: 'county', code, name: loc.county, country, state });
        }
      }
    }

    return Array.from(countries.values()); // todo return the map? sort it?
  }
}

// TODO: Merge in a hotspot helper?
type GeoCountry = Country & {
  states: GeoState[]
  // counties: GeoCounty[] // TODO: Do I need this
}

type GeoState = State & {
  country: GeoCountry
  counties: GeoCounty[]
}

type GeoCounty = County & {
  country: GeoCountry
  state: GeoState
}
